import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  PlusIcon, TrashIcon, WarningIcon, IdentificationBadgeIcon, UsersThreeIcon, DownloadSimpleIcon,
} from "@phosphor-icons/react";

const EMPTY = {
  company_name: "", contact_name: "", ein: "", phone: "", email: "",
  address: "", trade: "", notes: "", requires_1099: true, w9_on_file: false, active: true, portal_password: "",
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const SubcontractorsPanel = ({ subs, reloadSubs }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [downloading, setDownloading] = useState(false);
  const [jobsFor, setJobsFor] = useState(null);
  const [jobsData, setJobsData] = useState({ total_paid: 0, jobs: [] });
  const [jobsLoading, setJobsLoading] = useState(false);

  const w9Needed = (subs || []).filter((s) => s.over_1099_threshold && !s.w9_on_file);

  const openJobs = async (s) => {
    setJobsFor(s);
    setJobsLoading(true);
    setJobsData({ total_paid: 0, settled: 0, outstanding: 0, jobs: [] });
    try {
      const res = await api.get(`/admin/subcontractors/${s.id}/jobs`);
      setJobsData(res.data);
    } catch (e) {
      toast.error("Failed to load jobs");
    } finally {
      setJobsLoading(false);
    }
  };

  const togglePaid = async (j) => {
    try {
      await api.patch(`/admin/requests/${j.request_id}/subcontractors/${jobsFor.id}/paid`, { paid: !j.paid });
      const res = await api.get(`/admin/subcontractors/${jobsFor.id}/jobs`);
      setJobsData(res.data);
      reloadSubs();
    } catch (e) {
      toast.error("Failed to update payment status");
    }
  };

  const download1099 = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/subcontractors/1099-export", { params: { year }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `1099-report-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`1099 report for ${year} downloaded`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...EMPTY, ...s });
    setOpen(true);
  };

  const save = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const body = {
        company_name: form.company_name, contact_name: form.contact_name, ein: form.ein,
        phone: form.phone, email: form.email, address: form.address, trade: form.trade,
        notes: form.notes, requires_1099: !!form.requires_1099, w9_on_file: !!form.w9_on_file,
        active: form.active !== false, portal_password: form.portal_password || "",
      };
      if (editing) await api.patch(`/admin/subcontractors/${editing.id}`, body);
      else await api.post("/admin/subcontractors", body);
      toast.success(editing ? "Subcontractor updated" : "Subcontractor added");
      setOpen(false);
      reloadSubs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.company_name}? This also removes them from any assigned jobs.`)) return;
    try {
      await api.delete(`/admin/subcontractors/${s.id}`);
      toast.success("Subcontractor deleted");
      reloadSubs();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="subs-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
            <UsersThreeIcon size={22} weight="duotone" className="text-gold-600" /> Subcontractor Registry
          </h2>
          <p className="text-sm text-slate-500 mt-1">Internal only · EIN, 1099 status &amp; year-to-date pay tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="1099-year-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-[38px] rounded-sm border border-slate-300 bg-white text-sm px-2 text-slate-700"
          >
            {[0, 1, 2].map((d) => {
              const y = new Date().getFullYear() - d;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          <button
            data-testid="download-1099-btn"
            onClick={download1099}
            disabled={downloading}
            className="rounded-sm border border-slate-300 text-slate-700 hover:border-gold-600 text-sm font-semibold px-3 py-2.5 transition-colors active:scale-95 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <DownloadSimpleIcon size={16} /> {downloading ? "Exporting…" : "1099 report"}
          </button>
          <button
            data-testid="add-sub-btn"
            onClick={openNew}
            className="rounded-sm bg-navy-900 hover:bg-navy-800 text-white text-sm font-semibold px-4 py-2.5 transition-colors active:scale-95 inline-flex items-center gap-1.5"
          >
            <PlusIcon size={16} weight="bold" /> Add subcontractor
          </button>
        </div>
      </div>

      {w9Needed.length > 0 && (
        <div data-testid="w9-alert" className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-sm p-4">
          <WarningIcon size={20} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">Collect a W-9 before tax time</p>
            <p className="text-sm text-red-700 mt-0.5">
              {w9Needed.length} subcontractor{w9Needed.length > 1 ? "s have" : " has"} crossed $600 this year but {w9Needed.length > 1 ? "have" : "has"} no W-9 on file: {w9Needed.map((s) => s.company_name).join(", ")}.
            </p>
          </div>
        </div>
      )}

      {subs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-slate-400">
          No subcontractors yet. Add your first one to start assigning jobs.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="subs-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-4 font-semibold">Company</th>
                <th className="px-5 py-4 font-semibold">Trade</th>
                <th className="px-5 py-4 font-semibold">EIN</th>
                <th className="px-5 py-4 font-semibold">1099</th>
                <th className="px-5 py-4 font-semibold">YTD Paid</th>
                <th className="px-5 py-4 font-semibold">Jobs</th>
                <th className="px-5 py-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} data-testid={`sub-row-${s.id}`} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-4">
                    <p className="font-medium text-slate-900">{s.company_name}</p>
                    <p className="text-xs text-slate-500">{s.contact_name || "—"}{s.phone ? ` · ${s.phone}` : ""}</p>
                  </td>
                  <td className="px-5 py-4 text-slate-700">{s.trade || "—"}</td>
                  <td className="px-5 py-4 font-mono text-slate-700">{s.ein || "—"}</td>
                  <td className="px-5 py-4">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase ${s.requires_1099 ? "bg-gold-100 text-gold-800" : "bg-slate-200 text-slate-600"}`}>
                        <IdentificationBadgeIcon size={12} weight="fill" /> {s.requires_1099 ? "1099 req" : "No 1099"}
                      </span>
                      <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase ${s.w9_on_file ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>
                        {s.w9_on_file ? "W-9 on file" : "W-9 missing"}
                      </span>
                      {s.over_1099_threshold && !s.w9_on_file && (
                        <span data-testid={`sub-w9-needed-${s.id}`} className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase bg-red-600 text-white"><WarningIcon size={12} weight="fill" /> Collect W-9</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="font-semibold text-slate-900">{money(s.ytd_paid)}</span>
                    {s.over_1099_threshold && (
                      <span data-testid={`sub-threshold-${s.id}`} className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase bg-gold-600 text-white">
                        <WarningIcon size={12} weight="fill" /> 1099 due
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-slate-700">{s.project_count}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-3">
                      <button data-testid={`view-jobs-${s.id}`} onClick={() => openJobs(s)} className="text-sm font-semibold text-gold-700 hover:text-gold-800 transition-colors">Jobs</button>
                      <button data-testid={`edit-sub-${s.id}`} onClick={() => openEdit(s)} className="text-sm font-semibold text-navy-700 hover:text-navy-900 transition-colors">Edit</button>
                      <button data-testid={`delete-sub-${s.id}`} onClick={() => remove(s)} className="text-slate-400 hover:text-red-600 transition-colors"><TrashIcon size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-slate-400 mt-3">A subcontractor is flagged <span className="font-semibold text-gold-700">1099 due</span> once their year-to-date pay reaches $600.</p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="sub-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit subcontractor" : "Add subcontractor"}</DialogTitle>
            <DialogDescription>Stored internally for project assignment and 1099 tracking.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="mb-1 block">Company / Business name *</Label>
              <Input data-testid="sub-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="bg-white border-slate-300" />
            </div>
            <div>
              <Label className="mb-1 block">Contact name</Label>
              <Input data-testid="sub-contact" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="bg-white border-slate-300" />
            </div>
            <div>
              <Label className="mb-1 block">EIN</Label>
              <Input data-testid="sub-ein" value={form.ein} onChange={(e) => set("ein", e.target.value)} placeholder="12-3456789" className="bg-white border-slate-300 font-mono" />
            </div>
            <div>
              <Label className="mb-1 block">Phone</Label>
              <Input data-testid="sub-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white border-slate-300" />
            </div>
            <div>
              <Label className="mb-1 block">Email</Label>
              <Input data-testid="sub-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-white border-slate-300" />
            </div>
            <div className="col-span-2">
              <Label className="mb-1 block">Trade / Specialty</Label>
              <Input data-testid="sub-trade" value={form.trade} onChange={(e) => set("trade", e.target.value)} placeholder="e.g. Electrical, Roofing" className="bg-white border-slate-300" />
            </div>
            <div className="col-span-2">
              <Label className="mb-1 block">Address</Label>
              <Input data-testid="sub-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-white border-slate-300" />
            </div>
            <div className="col-span-2">
              <Label className="mb-1 block">Notes</Label>
              <Textarea data-testid="sub-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-white border-slate-300" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="sub-requires-1099" checked={!!form.requires_1099} onChange={(e) => set("requires_1099", e.target.checked)} />
              1099 required
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="sub-w9" checked={!!form.w9_on_file} onChange={(e) => set("w9_on_file", e.target.checked)} />
              W-9 on file
            </label>
            <div className="col-span-2">
              <Label className="mb-1 block">Portal password <span className="text-xs font-normal text-slate-400">(optional — gives this sub login access at /portal)</span></Label>
              <Input data-testid="sub-portal-password" type="password" value={form.portal_password} onChange={(e) => set("portal_password", e.target.value)} placeholder={editing ? "Leave blank to keep current" : "Set a login password"} className="bg-white border-slate-300" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setOpen(false)} className="rounded-sm border border-slate-300 text-slate-700 text-sm font-semibold px-4 py-2 hover:bg-slate-50 transition-colors">Cancel</button>
            <button data-testid="save-sub-btn" onClick={save} disabled={saving} className="rounded-sm bg-gold-600 hover:bg-gold-700 text-white text-sm font-semibold px-5 py-2 transition-colors active:scale-95 disabled:opacity-50">
              {saving ? "Saving…" : editing ? "Save changes" : "Add subcontractor"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!jobsFor} onOpenChange={(o) => !o && setJobsFor(null)}>
        <DialogContent className="max-w-lg" data-testid="sub-jobs-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{jobsFor?.company_name} — Job history</DialogTitle>
            <DialogDescription>Every job assigned to this subcontractor and what they're owed.</DialogDescription>
          </DialogHeader>
          {jobsLoading ? (
            <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-center"><p className="text-[11px] uppercase tracking-wide text-slate-500">Total</p><p data-testid="sub-jobs-total" className="font-heading text-lg font-extrabold text-slate-900">{money(jobsData.total_paid)}</p></div>
                <div className="bg-green-50 border border-green-200 rounded-sm px-3 py-2 text-center"><p className="text-[11px] uppercase tracking-wide text-green-700">Paid</p><p data-testid="sub-jobs-settled" className="font-heading text-lg font-extrabold text-green-700">{money(jobsData.settled)}</p></div>
                <div className="bg-gold-50 border border-gold-200 rounded-sm px-3 py-2 text-center"><p className="text-[11px] uppercase tracking-wide text-gold-700">Outstanding</p><p data-testid="sub-jobs-outstanding" className="font-heading text-lg font-extrabold text-gold-700">{money(jobsData.outstanding)}</p></div>
              </div>
              {(jobsData.jobs || []).length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No jobs assigned yet.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {jobsData.jobs.map((j, i) => (
                    <div key={i} data-testid={`sub-job-${j.ref}`} className="flex items-center justify-between gap-3 border border-slate-200 rounded-sm px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">{j.ref} <span className="font-normal text-slate-500">· {j.customer}</span></p>
                        <p className="text-xs text-slate-500 capitalize">{j.status}{j.scheduled_date ? ` · ${j.scheduled_date}` : ""}{j.assigned_at ? ` · assigned ${String(j.assigned_at).slice(0, 10)}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {j.response && j.response !== "pending" && (
                          <span data-testid={`job-resp-${j.ref}`} className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${j.response === "accepted" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>{j.response}</span>
                        )}
                        <span className="font-semibold text-slate-900">{money(j.pay_amount)}</span>
                        <button data-testid={`toggle-paid-${j.ref}`} onClick={() => togglePaid(j)} className={`rounded-sm text-xs font-bold uppercase px-2.5 py-1 transition-colors ${j.paid ? "bg-green-600 text-white hover:bg-green-700" : "border border-slate-300 text-slate-600 hover:border-green-600"}`}>{j.paid ? "Paid ✓" : "Mark paid"}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
